import { validateBrDocument, type BrDocumentType } from '@/lib/br-document';

const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export function isValidEmail(value: string): boolean {
  const email = value.trim().toLowerCase();
  if (!email || email.length > 254) return false;
  return EMAIL_RE.test(email);
}

export function isValidBrPhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 11) return false;
  const ddd = Number(digits.slice(0, 2));
  if (ddd < 11 || ddd > 99) return false;
  if (digits.length === 11 && digits[2] !== '9') return false;
  return true;
}

export interface TrialFormValues {
  pessoa: 'pf' | 'pj';
  doc: string;
  nome: string;
  email: string;
  tel: string;
  termsAccepted: boolean;
}

export type AccountFormValues = Omit<TrialFormValues, 'termsAccepted'>;

/** Dados da conta (step 1). Sem termos — eles ficam no step de pagamento. */
export function validateAccountForm(values: AccountFormValues): string | null {
  const docDigits = values.doc.replace(/\D/g, '');
  const docType: BrDocumentType = values.pessoa === 'pf' ? 'cpf' : 'cnpj';

  if (values.pessoa === 'pf' && docDigits.length !== 11) {
    return 'Informe um CPF com 11 dígitos.';
  }
  if (values.pessoa === 'pj' && docDigits.length !== 14) {
    return 'Informe um CNPJ com 14 caracteres.';
  }

  if (!validateBrDocument(docType, values.doc)) {
    return values.pessoa === 'pf' ? 'CPF inválido. Confira os números.' : 'CNPJ inválido. Confira os números.';
  }

  if (!values.nome.trim() || values.nome.trim().length < 2) {
    return 'Informe seu nome completo.';
  }

  if (!isValidEmail(values.email)) {
    return 'Informe um e-mail válido (ex.: voce@empresa.com.br).';
  }

  if (!isValidBrPhone(values.tel)) {
    return 'Informe um telefone válido com DDD (ex.: (11) 99000-0000).';
  }

  return null;
}

export function validateTrialForm(values: TrialFormValues): string | null {
  return (
    validateAccountForm(values) ??
    (!values.termsAccepted ? 'Aceite os termos para continuar.' : null)
  );
}

export interface CardFormValues {
  cardNumber: string;
  cardName: string;
  cardExpiry: string; // MM/AA
  cardCcv: string;
  cep: string;
  addressNumber: string;
}

function luhnValid(digits: string): boolean {
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = Number(digits[i]);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

export function validateCardForm(values: CardFormValues): string | null {
  const num = values.cardNumber.replace(/\D/g, '');
  if (num.length < 13 || num.length > 19 || !luhnValid(num)) {
    return 'Número de cartão inválido. Confira os dígitos.';
  }
  if (!values.cardName.trim() || values.cardName.trim().length < 3) {
    return 'Informe o nome impresso no cartão.';
  }
  const m = values.cardExpiry.match(/^(\d{2})\/(\d{2})$/);
  if (!m) return 'Validade do cartão no formato MM/AA.';
  const month = Number(m[1]);
  const year = 2000 + Number(m[2]);
  if (month < 1 || month > 12) return 'Mês de validade inválido.';
  const now = new Date();
  if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) {
    return 'Cartão vencido. Use outro cartão.';
  }
  if (!/^\d{3,4}$/.test(values.cardCcv)) return 'CVV inválido (3 ou 4 dígitos).';
  if (values.cep.replace(/\D/g, '').length !== 8) return 'CEP inválido (8 dígitos).';
  if (!values.addressNumber.trim()) return 'Informe o número do endereço de cobrança.';
  return null;
}
